import { useQuery } from "@tanstack/react-query";
import AugustDigitalSDK from "@augustdigital/pools";
import { yieldVaultContractConfig } from "@config/contractsConfig";
import { useTVL } from "@stader-labs/web-sdk";
import { useMemo } from "react";
import axios from "axios";
import { mainnet } from "viem/chains";
import { getGainApy } from "@services/index";
import { toFixedWithoutRounding } from "@constants/common";
import { numberToCurrency } from "utils";
import { useIdle } from "react-use";

const ALCHEMY_ETHEREUM_KEY_AUGUST = "YrvmbFCZJH62ykN4_0JW0DQbemITDOBC";

const UNIVERSE_API_ENDPOINT = process.env.NEXT_PUBLIC_UNIVERSE_API_URL;

const AugustSDK = new AugustDigitalSDK({
  providers: {
    1: `https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_ETHEREUM_KEY_AUGUST}`,
  },
});

const useYieldVaultData = () => {
  const tvl = useTVL();
  const isIdle = useIdle(150e3);

  const { data: apyData, isLoading: isLoadingApy } = useQuery({
    queryKey: ["gainApy"],
    queryFn: async () => {
      const { data } = await getGainApy();
      return data || {};
    },
    enabled: !isIdle,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const ethPrice = useMemo(() => {
    if (tvl.tvl?.value && tvl.tvl.usdTvl) {
      return tvl?.tvl?.usdTvl / tvl.tvl?.value;
    }
    return 0;
  }, [tvl]);

  const { isLoading, data } = useQuery({
    queryKey: ["yield-gain-pool"],
    queryFn: async () => {
      const r = await AugustSDK.getPool(
        yieldVaultContractConfig[mainnet.id].interactionPool.address
      );
      return r;
    },
    enabled: !isIdle,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const { data: exchangeRateData, isLoading: isLoadingExchangeRate } = useQuery(
    {
      queryKey: ["eth-exchange"],
      queryFn: async () => {
        const r = await axios.get(
          `${UNIVERSE_API_ENDPOINT}/rseth/exchangeRate/?lrtToken=ETH`
        );
        return r;
      },
      enabled: !isIdle,
      staleTime: 60_000,
      refetchInterval: 60_000,
    }
  );

  const totalTVL = useMemo(() => {
    if (
      exchangeRateData?.data?.value &&
      ethPrice &&
      data?.totalAssets?.normalized
    ) {
      return (
        exchangeRateData?.data?.value *
        ethPrice *
        Number(data.totalAssets.normalized)
      );
    }
    return null;
  }, [exchangeRateData?.data?.value, ethPrice, data?.totalAssets?.normalized]);

  const apy: number | null =
    apyData?.hgETH && !isNaN(+apyData?.hgETH) ? +apyData?.hgETH : null;

  return {
    isLoadingAPYData: isLoadingApy || isLoading,
    isLoadingTotalStaked: isLoading || isLoadingExchangeRate,
    apy: apyData?.hgETH,
    totalStaked: totalTVL,
    tvl: totalTVL,
    ethStaked:
      exchangeRateData?.data?.value && data?.totalAssets?.normalized
        ? exchangeRateData?.data?.value * Number(data?.totalAssets?.normalized)
        : "",
    formattedApy: apy ? `${toFixedWithoutRounding(apy.toString(), 2)}%` : "-",
    formattedTvl:
      totalTVL && !isNaN(Number(totalTVL))
        ? `${numberToCurrency(totalTVL.toFixed(2), 1)}`
        : "-",
    formattedETHStaked:
      data?.totalAssets?.normalized && exchangeRateData?.data?.value
        ? `${toFixedWithoutRounding(
            (
              exchangeRateData?.data?.value *
              Number(data?.totalAssets?.normalized)
            ).toString(),
            2
          )} ETH`
        : "",
  };
};

export default useYieldVaultData;
